import React, { useCallback } from 'react';
import { Wand2, Plus, Trash2, Copy, AlertCircle, Eraser } from 'lucide-react';
import SecondaryPhotosPanel from 'scenes/LandingPage/components/SecondaryPhotosPanel';

interface PromptBuilderProps {
  selectedColumns: string[];
  promptRules: PromptRule[];
  onPromptRulesChange: (rules: PromptRule[]) => void;
  treatmentName: string;
  onNameChange: (name: string) => void;
  imageColumn: string | null;
  onNext: () => void;
  mediaType: MediaType;
  removeBackground: boolean;
  onRemoveBackgroundChange: (value: boolean) => void;
}

const PromptBuilder: React.FC<PromptBuilderProps> = ({
  selectedColumns,
  promptRules,
  onPromptRulesChange,
  treatmentName,
  onNameChange,
  imageColumn,
  onNext,
  mediaType,
  removeBackground,
  onRemoveBackgroundChange
}) => {
  const addRule = () => {
    const newRule: PromptRule = {
      id: `rule-${Date.now()}`,
      condition: {
        column: selectedColumns[0],
        operator: '=',
        value: ''
      },
      prompt: '',
      useBrandGuidelines: false,
      referenceImages: [],
      removeBackground: false
    };
    onPromptRulesChange([...promptRules, newRule]);
  };

  const updateRule = useCallback((id: string, updates: Partial<PromptRule>) => {
    onPromptRulesChange(
      promptRules.map(rule => 
        rule.id === id ? { ...rule, ...updates } : rule
      )
    );
  }, [promptRules, onPromptRulesChange]);

  const handleSecondaryUpload = useCallback(async (id: string, files: File[], category?: string) => {
      const items: SecondaryImage[] = files.map((f, i) => ({
        id: `${Date.now()}-${i}`,
        file: f,
        url: URL.createObjectURL(f),
        alt: f.name,
        category: category || 'template'
      }))
      updateRule(id, {
        referenceImages: [...(promptRules.find(r => r.id === id)?.referenceImages || []), ...items]
      })
    }, [promptRules, updateRule]);
  
  const handleSecondaryRemove = useCallback(async (id: string, img: SecondaryImage) => {
    if (img.url.startsWith('blob:')) URL.revokeObjectURL(img.url);
    updateRule(id, {
      referenceImages: (promptRules.find(r => r.id === id)?.referenceImages || []).filter(x => x.id !== img.id)
    });
  }, [promptRules, updateRule]);

  const handleSecondaryCategoryChange = useCallback((id: string, img: SecondaryImage, newCategory: string) => {
    updateRule(id, {
      referenceImages: (promptRules.find(r => r.id === id)?.referenceImages || []).map(x => 
        x.id === img.id ? { ...x, category: newCategory } : x
      )
    });
  }, [promptRules, updateRule]);

  const deleteRule = (id: string) => {
    onPromptRulesChange(promptRules.filter(rule => rule.id !== id));
  };

  const duplicateRule = (rule: PromptRule) => {
    const newRule = {
      ...rule,
      id: `rule-${Date.now()}`,
    };
    onPromptRulesChange([...promptRules, newRule]);
  };

  const insertVariable = (ruleId: string, columnName: string) => {
    const rule = promptRules.find(r => r.id === ruleId);
    if (rule) {
      updateRule(ruleId, { prompt: rule.prompt + `{${columnName}}` });
    }
  };

  const hasValidPromptRules = promptRules.length > 0 && 
                              promptRules.every(r => r.prompt.trim() !== '' && r.condition.value.trim() !== '');

  // Background removal only counts for images
  const effectiveRemoveBackground = mediaType === 'image' && removeBackground;

  const canProceed = treatmentName.trim() !== '' && 
                     imageColumn !== null &&
                     (hasValidPromptRules || effectiveRemoveBackground);

  const getBrandGuidelinesCheckbox = (rule: PromptRule) => (
    <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-300">Use Brand Guidelines</span>
        <label 
            htmlFor={`brand-guidelines-toggle-${rule.id}`}
            className={'flex items-center gap-2 cursor-pointer transition-opacity'}
        >
            <div className="relative">
                <input
                    id={`brand-guidelines-toggle-${rule.id}`}
                    type="checkbox"
                    className="sr-only peer"
                    checked={rule.useBrandGuidelines || false}
                    onChange={() => updateRule(rule.id, { useBrandGuidelines: !(rule.useBrandGuidelines || false) })}
                />
                <div className="block w-10 h-5 bg-white/20 rounded-full peer-checked:bg-blue-600 transition"></div>
                <div className="dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
            </div>
        </label>
    </div>
  );

  const getBackgroundRemoverCheckbox = (rule: PromptRule) => (
    <div className="flex items-center justify-between gap-2">
        <Eraser className="p-1 text-gray-400 hover:text-blue-400 transition-colors" />
        <label 
            htmlFor={`remove-background-toggle-${rule.id}`}
            className={'flex items-center gap-2 cursor-pointer transition-opacity'}
        >
            <div className="relative">
                <input
                    id={`remove-background-toggle-${rule.id}`}
                    type="checkbox"
                    className="sr-only peer"
                    checked={rule.removeBackground || false}
                    onChange={() => updateRule(rule.id, { removeBackground: !(rule.removeBackground || false) })}
                />
                <div className="block w-10 h-5 bg-white/20 rounded-full peer-checked:bg-blue-600 transition"></div>
                <div className="dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
            </div>
        </label>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Wand2 className="w-6 h-6 text-purple-400" />
        <h2 className="text-xl font-semibold text-white">Build Conditional Prompts</h2>
      </div>

      <p className="text-sm text-gray-400">
        Create rules that apply different prompts based on your data. The AI will use input images to generate {mediaType === 'image' ? 'new images' : 'videos'} based on your prompts.
      </p>

      {/* Treatment Name */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Treatment Name
        </label>
        <input
          type="text"
          value={treatmentName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={`e.g., ${mediaType === 'image' ? 'Product Image Generator' : 'Product Video Generator'}`}
          className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition"
        />
      </div>

      {/* Background Removal Toggle */}
      {mediaType === 'image' && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Eraser className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Remove Background</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Make image backgrounds transparent (PNG output)
                </p>
              </div>
            </div>
            <button
              onClick={() => onRemoveBackgroundChange(!removeBackground)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                removeBackground ? 'bg-emerald-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  removeBackground ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {removeBackground && (
            <div className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 rounded px-3 py-2">
              ✓ Background removal will be applied to all processed images
              {promptRules.length === 0 && (
                <span className="block mt-1 text-emerald-400">
                  💡 You can proceed without prompt rules - only background removal will be applied
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Available Variables */}
      {selectedColumns.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Available Variables (click to insert)
          </label>
          <div className="flex flex-wrap gap-2">
            {selectedColumns.map((column) => (
              <span
                key={column}
                className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-lg text-sm font-mono text-purple-300"
              >
                {`{${column}}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Prompt Rules */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Prompt Rules</h3>
            {effectiveRemoveBackground && promptRules.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">(Optional when global background removal is enabled)</p>
            )}
          </div>
          <button
            onClick={addRule}
            disabled={selectedColumns.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>

        {promptRules.length === 0 ? (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-400 mb-4">
              {effectiveRemoveBackground 
                ? 'No rules defined - background removal will apply to all images'
                : 'No rules defined yet'
              }
            </p>
            <button
              onClick={addRule}
              disabled={selectedColumns.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              {effectiveRemoveBackground ? 'Add Rule (Optional)' : 'Add Your First Rule'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {promptRules.map((rule, index) => (
              <div
                key={rule.id}
                className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 space-y-3"
              >
                {/* Rule Header */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-300">
                    Rule {index + 1}
                  </span>
                  <div className="flex gap-2">
                    { !removeBackground && mediaType === 'image' && <React.Fragment>
                        { getBackgroundRemoverCheckbox(rule)} 
                        |
                      </React.Fragment>
                    }
                    { getBrandGuidelinesCheckbox(rule) }
                    |
                    <button
                      onClick={() => duplicateRule(rule)}
                      className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors"
                      title="Duplicate rule"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteRule(rule.id)}
                      className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete rule"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Condition (IF statement) */}
                <div className="bg-black/30 rounded-lg p-3 space-y-2">
                  <label className="text-xs text-gray-400 uppercase tracking-wide">
                    When (Condition)
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {/* Column Select */}
                    <select
                      value={rule.condition.column}
                      onChange={(e) => updateRule(rule.id, {
                        condition: { ...rule.condition!, column: e.target.value }
                      })}
                      className="bg-gray-800 border border-gray-600 text-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    >
                      {selectedColumns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>

                    {/* Operator Select */}
                    <select
                      value={rule.condition.operator}
                      onChange={(e) => updateRule(rule.id, {
                        condition: { ...rule.condition!, operator: e.target.value as any }
                      })}
                      className="bg-gray-800 border border-gray-600 text-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    >
                      <option value="=">equals (=)</option>
                      <option value="!=">not equals (≠)</option>
                      <option value="contains">contains</option>
                      <option value="starts_with">starts with</option>
                      <option value="ends_with">ends with</option>
                      <option value=">">greater than (&gt;)</option>
                      <option value="<">less than (&lt;)</option>
                      <option value=">=">greater or equal (≥)</option>
                      <option value="<=">less or equal (≤)</option>
                    </select>

                    {/* Value Input */}
                    <input
                      type="text"
                      value={rule.condition.value}
                      onChange={(e) => updateRule(rule.id, {
                        condition: { ...rule.condition!, value: e.target.value }
                      })}
                      placeholder="value"
                      className="bg-gray-800 border border-gray-600 text-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    Example: <span className="text-gray-400">{rule.condition.column} {rule.condition.operator} "{rule.condition.value || '...'}"</span>
                  </p>
                </div>

                {/* Reference Images */}
                <div className="space-y-2">
                  <SecondaryPhotosPanel
                    images={rule.referenceImages || []}
                    onUpload={(files, category) => handleSecondaryUpload(rule.id, files, category)}
                    onRemove={(img) => handleSecondaryRemove(rule.id, img)}
                    categories={[
                      { value: 'template', label: 'Template' },
                      { value: 'reference', label: 'Reference' },
                      { value: 'style', label: 'Style' },
                      { value: 'product', label: 'Product' },
                      { value: 'background', label: 'Background' },
                      { value: 'character', label: 'Character' },
                      { value: 'texture', label: 'Texture' },
                    ]}
                    onCategoryChange={(img, newCategory) => handleSecondaryCategoryChange(rule.id, img, newCategory)}
                    title="Reference Images"
                    subtitle="Add reference images to guide the AI's generation"
                    cta="Add Reference Images"
                  />
                </div>

                {/* Prompt Template (THEN statement) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-gray-400 uppercase tracking-wide">
                      Then (Prompt)
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {selectedColumns.slice(0, 3).map((column) => (
                        <button
                          key={column}
                          onClick={() => insertVariable(rule.id, column)}
                          className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded text-xs font-mono text-purple-300 hover:bg-purple-500/30 transition-all"
                        >
                          +{column}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={rule.prompt}
                    onChange={(e) => updateRule(rule.id, { prompt: e.target.value })}
                    placeholder="Prompt template for this condition..."
                    rows={3}
                    className="w-full bg-gray-800 border border-gray-600 text-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none font-mono"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Warnings */}
      {selectedColumns.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-300 font-medium">No columns selected</p>
            <p className="text-xs text-yellow-400 mt-1">
              Please select at least one column above to create conditional rules.
            </p>
          </div>
        </div>
      )}

      {promptRules.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-300 font-medium">Only matching rows will be processed</p>
            <p className="text-xs text-blue-400 mt-1">
              Rows that don't match any rule will be skipped. The AI will generate {mediaType === 'image' ? 'new images' : 'videos'} for matching rows.
            </p>
          </div>
        </div>
      )}

      {/* Example */}
      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Example</h3>
        <div className="space-y-2 text-sm font-mono text-gray-400">
          <div className="flex gap-2">
            <span className="text-purple-400">IF</span>
            <span>product_type = "Phone"</span>
            <span className="text-green-400">THEN</span>
            <span className="text-gray-300">"Generate a lifestyle {mediaType === 'image' ? 'image' : 'video'} showing {'{product_name}'} in use"</span>
          </div>
          <div className="flex gap-2">
            <span className="text-purple-400">IF</span>
            <span>product_type = "Laptop"</span>
            <span className="text-green-400">THEN</span>
            <span className="text-gray-300">"Create a professional {mediaType === 'image' ? 'product shot' : 'demo video'} of {'{product_name}'}"</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 Only rows matching these conditions will be processed
          </p>
        </div>
      </div>

      {/* Next Button */}
      <button
        onClick={onNext}
        disabled={!canProceed}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 flex items-center justify-center gap-2"
      >
        Preview Treatment
        <span className="text-xs opacity-75">
          ({promptRules.length} rule{promptRules.length !== 1 ? 's' : ''}
          {effectiveRemoveBackground && ' + bg removal'})
        </span>
      </button>
    </div>
  );
};

export default PromptBuilder;
